// ProgressivePage.tsx - Enhanced with Safari Memory Management
import { useState, useEffect, useCallback, FC, useRef, useContext } from 'react'
import { usePageRenderer } from '../hooks/usePageRenderer'
import { safariMemoryManager } from '../helpers/SafariMemoryManager'
import type { CSSProperties } from 'react'
import './Core.css'
import { Page } from 'react-pdf'
import { observer } from 'mobx-react'
import { StoreContext } from '@store/Root.store'
import {
  EditableFieldsOverlay,
  type EditableFieldsConfig,
} from '@/components/EditableFields/EditableFieldsOverlay'
import type { EditableField } from '@/components/SignDocumentCommon/types'
import { isPdfTextItem, resolveFieldsForPage } from '@/methods/resolveEditableFields'

interface LoadedPdfPage {
  originalWidth: number
  originalHeight: number
  getTextContent: () => Promise<{ items: unknown[] }>
}

// Types for render task tracking
interface PageRenderTask {
  cancel: () => void
  pageNumber: number
  timestamp: number
  isActive: boolean
}

interface ProgressivePageProps {
  pageNumber: number
  width: number
  pageAspectRatio?: number
  onMemoryChange?: (
    pageNumber: number,
    width: number,
    height: number,
    pixelRatio: number,
    action: 'add' | 'remove'
  ) => void
  registerRenderTask?: (pageNumber: number, renderTask: unknown) => void
  unregisterRenderTask?: (pageNumber: number) => void
  editableFields?: EditableFieldsConfig
}

export const ProgressivePage: FC<ProgressivePageProps> = observer(
  ({
    pageNumber,
    width,
    pageAspectRatio = 1.414,
    onMemoryChange,
    registerRenderTask,
    unregisterRenderTask,
    editableFields,
  }) => {
    const store = useContext(StoreContext)
    const { pageScale } = store.PdfViewerState

    const renderState = usePageRenderer(pageNumber)
    const [hasError, setHasError] = useState(false)
    const [renderAttempts, setRenderAttempts] = useState(0)
    // Page size in PDF points (viewport at scale 1) — needed to place absolute-point fields.
    const [pagePointSize, setPagePointSize] = useState<{ width: number; height: number }>()
    // Editable fields resolved to absolute positions on this page.
    const [resolvedFields, setResolvedFields] = useState<EditableField[]>([])
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const renderTaskRef = useRef<unknown>(null)
    const pageProxyRef = useRef<LoadedPdfPage | null>(null)

    // Calculate container height to match itemHeight in Renderer.tsx
    // This ensures FixedSizeList scrolling matches actual content height
    const containerHeight = width * pageAspectRatio * pageScale + 24
    const height = width * pageAspectRatio
    const capabilities = safariMemoryManager.getBrowserCapabilities()

    // Get optimal render settings based on Safari memory constraints
    const renderSettings = safariMemoryManager.getOptimalRenderSettings(width, height)

    const handleRenderSuccess = useCallback(() => {
      setHasError(false)
      setRenderAttempts(0)

      // Track memory usage
      onMemoryChange?.(
        pageNumber,
        renderSettings.width,
        renderSettings.height,
        renderSettings.devicePixelRatio,
        'add'
      )

      // Safari-specific post-render optimizations
      if (capabilities.isSafari && canvasRef.current) {
        // Force canvas to release unused memory
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        if (ctx) {
          // Clear any off-screen areas that might be cached
          ctx.clearRect(canvas.width, 0, 1, 1)
        }
      }
    }, [
      pageNumber,
      renderSettings.width,
      renderSettings.height,
      renderSettings.devicePixelRatio,
      onMemoryChange,
      capabilities.isSafari,
    ])

    const handleRenderError = useCallback(() => {
      setRenderAttempts((prev) => prev + 1)

      // Mark as error after retries
      if (renderAttempts >= 2) {
        setHasError(true)
      }
    }, [renderAttempts])

    // Custom render task tracking - integrates with PDF.js render pipeline
    const handleRenderStart = useCallback(() => {
      // Create a render task object that can be cancelled
      // This represents the active rendering process for this page
      const renderTask: PageRenderTask = {
        cancel: () => {
          // Force re-render to stop current rendering
          setRenderAttempts((prev) => prev + 1)
        },
        pageNumber,
        timestamp: Date.now(),
        isActive: true,
      }

      renderTaskRef.current = renderTask
      registerRenderTask?.(pageNumber, renderTask)
    }, [pageNumber, registerRenderTask])

    // Enhanced render success handler that clears render task tracking
    const handleRenderSuccessWithTracking = useCallback(() => {
      handleRenderSuccess()

      // Clear the render task from tracking since rendering is complete
      if (renderTaskRef.current) {
        renderTaskRef.current = null
      }
    }, [handleRenderSuccess])

    // Enhanced render error handler that clears render task tracking
    const handleRenderErrorWithTracking = useCallback(() => {
      handleRenderError()

      // Clear the render task from tracking since rendering failed
      if (renderTaskRef.current) {
        renderTaskRef.current = null
      }
    }, [handleRenderError])

    // Start tracking when component renders
    useEffect(() => {
      if (renderState.state === 'ready') {
        handleRenderStart()
      }

      return () => {
        // Cleanup render task when component unmounts or state changes
        if (renderTaskRef.current) {
          unregisterRenderTask?.(pageNumber)
          renderTaskRef.current = null
        }
      }
    }, [renderState.state, handleRenderStart, pageNumber, unregisterRenderTask])

    // Reset error state when page changes
    useEffect(() => {
      setHasError(false)
      setRenderAttempts(0)
    }, [pageNumber])

    // Re-runs on page load and when the async-loaded fields arrive.
    const fields = editableFields?.fields
    const resolveFields = useCallback(async () => {
      const page = pageProxyRef.current
      if (!page || !fields?.length) {
        setResolvedFields([])
        return
      }

      try {
        const { items } = await page.getTextContent()
        setResolvedFields(
          resolveFieldsForPage(fields, items.filter(isPdfTextItem), pageNumber, page.originalHeight)
        )
      } catch {
        setResolvedFields([])
      }
    }, [fields, pageNumber])

    useEffect(() => {
      void resolveFields()
    }, [resolveFields])

    // Report placed fields upward so the sign gate knows which declared fields have rendered.
    const onFieldsResolved = editableFields?.onFieldsResolved
    useEffect(() => {
      if (onFieldsResolved && resolvedFields.length) {
        onFieldsResolved(resolvedFields.map((field) => field.key))
      }
    }, [resolvedFields, onFieldsResolved])

    // Cleanup memory when component unmounts or page changes
    useEffect(() => {
      return () => {
        // Cancel and unregister any active render task
        if (renderTaskRef.current) {
          unregisterRenderTask?.(pageNumber)
          renderTaskRef.current = null
        }

        // Release canvas resources for this page
        safariMemoryManager.releaseCanvas(pageNumber)

        // Notify memory manager
        onMemoryChange?.(
          pageNumber,
          renderSettings.width,
          renderSettings.height,
          renderSettings.devicePixelRatio,
          'remove'
        )
      }
    }, [
      pageNumber,
      unregisterRenderTask,
      onMemoryChange,
      renderSettings.width,
      renderSettings.height,
      renderSettings.devicePixelRatio,
    ])

    if (renderState.state === 'placeholder') {
      return (
        <div
          className="pdf-core__page-container"
          style={
            {
              '--pc-min-height': `${containerHeight}px`,
              '--pc-justify': pageScale > 1 ? 'flex-start' : 'center',
              '--pc-pointer-events': pageScale > 1 ? 'auto' : 'none',
            } as CSSProperties
          }
        >
          <div
            className="pdf-core__placeholder-page"
            style={
              {
                '--pp-width': `${width}px`,
                '--pp-height': `${height}px`,
              } as CSSProperties
            }
          >
            <div className="pdf-core__placeholder-text">Loading Page {pageNumber}</div>
            <div className="pdf-core__placeholder-subtext">Preparing content...</div>
          </div>
        </div>
      )
    }

    if (hasError) {
      return (
        <div
          className="pdf-core__page-container"
          style={
            {
              '--pc-min-height': `${containerHeight}px`,
              '--pc-justify': pageScale > 1 ? 'flex-start' : 'center',
              '--pc-pointer-events': pageScale > 1 ? 'auto' : 'none',
            } as CSSProperties
          }
        >
          <div
            className="pdf-core__placeholder-page"
            style={
              {
                '--pp-width': `${width}px`,
                '--pp-height': `${height}px`,
              } as CSSProperties
            }
          >
            <div className="pdf-core__placeholder-text">⚠️ Page {pageNumber}</div>
            <div className="pdf-core__placeholder-subtext">
              Render failed after {renderAttempts} attempts
            </div>
          </div>
        </div>
      )
    }

    // Calculate final render properties
    const finalScale = renderSettings.scale * pageScale
    const finalDevicePixelRatio = Math.min(
      renderSettings.devicePixelRatio,
      renderState.devicePixelRatio
    )

    return (
      <div
        className="pdf-core__page-container"
        style={
          {
            '--pc-min-height': `${containerHeight}px`,
            '--pc-justify': pageScale > 1 ? 'flex-start' : 'center',
            '--pc-pointer-events': pageScale > 1 ? 'auto' : 'none',
          } as CSSProperties
        }
      >
        <div
          className="pdf-core__pdf-page-wrapper"
          style={
            {
              '--ppw-max-width': (pageScale ?? 1) > 1 ? 'none' : '100%',
              position: 'relative',
            } as CSSProperties
          }
        >
          <Page
            key={`${pageNumber}-${renderAttempts}`} // Force re-render on retry
            pageNumber={pageNumber}
            width={renderSettings.width}
            scale={finalScale}
            devicePixelRatio={finalDevicePixelRatio}
            renderAnnotationLayer={true}
            renderTextLayer={true}
            onLoadSuccess={(page) => {
              // originalWidth/Height are intrinsic PDF points (scale 1); width/height are
              // the display px (scaled), which would break the point→% conversion on zoom.
              setPagePointSize({ width: page.originalWidth, height: page.originalHeight })
              pageProxyRef.current = page as unknown as LoadedPdfPage
              void resolveFields()
            }}
            onRenderSuccess={handleRenderSuccessWithTracking}
            onRenderError={handleRenderErrorWithTracking}
            canvasRef={canvasRef}
          />
          {editableFields && pagePointSize && resolvedFields.length > 0 && (
            <EditableFieldsOverlay
              {...editableFields}
              fields={resolvedFields}
              pageNumber={pageNumber}
              pageWidthPt={pagePointSize.width}
              pageHeightPt={pagePointSize.height}
            />
          )}
        </div>
      </div>
    )
  }
)
